'use client';

import { CaterpillarAndMoreSlot, CaterpillarItemSlot } from '@magicschool/spellbook/components/Caterpillar/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentProps, FC } from 'react';

export type CaterpillarProps = ComponentProps<'div'> &
  VariantProps<typeof caterpillarVariants> & {
    /** The amount of items to show before showing the "and more" slot. */
    maxItemsToShow?: number;
  };

export const caterpillarVariants = cva('group flex items-center rtl:flex-row-reverse ltr:flex-row', {
  variants: {
    /** The size of the caterpillar items. Be sure to use the same size components for the item children. */
    size: {
      sm: '-space-x-3',
      md: '-space-x-4',
      lg: '-space-x-5',
    },
    /** Whether to expand the items to make them all visible on hover. */
    expandOnHover: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      class: 'focus-within:space-x-1 hover:space-x-1 active:space-x-1',
      size: ['sm', 'md'],
      expandOnHover: true,
    },
    {
      class: 'focus-within:space-x-2 hover:space-x-2 active:space-x-2',
      size: 'lg',
      expandOnHover: true,
    },
  ],
});

const CaterpillarBase: FC<CaterpillarProps> = ({
  children,
  className,
  maxItemsToShow = 5,
  size = 'md',
  expandOnHover = false,
  ...props
}) => {
  const { slots } = useSlots(children, {
    items: { Component: CaterpillarItemSlot, props: { size } },
    andMore: { Component: CaterpillarAndMoreSlot, props: { size } },
  });

  if (!slots.items) {
    console.warn('Caterpillar: No items provided. Please use Caterpillar.Item to add items.');
    return null;
  }

  if (!Number.isInteger(maxItemsToShow) || maxItemsToShow < 0) {
    console.warn('Caterpillar: maxItemsToShow should be a non-negative integer. Defaulting to 5.');
    maxItemsToShow = 5;
  }

  /**
   * @TODO [SPE-37] Utility for post-slot prop updates
   * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
   */
  const andMoreSlot = slots.andMore;
  let andMoreProps;

  if (andMoreSlot && Array.isArray(slots.items)) {
    andMoreProps = { count: slots.items.length - maxItemsToShow || 0 };
  }

  if (andMoreSlot && andMoreProps && !Array.isArray(andMoreSlot)) {
    slots.andMore = {
      ...andMoreSlot,
      props: {
        ...(andMoreSlot.props || {}),
        ...andMoreProps,
      },
    };
  }

  return (
    <div data-spellbook-name="caterpillar" className={cn(caterpillarVariants({ size, expandOnHover }), className)} {...props}>
      {Array.isArray(slots.items) ? (
        slots.items.length > maxItemsToShow ? (
          <>
            {slots.items.slice(0, maxItemsToShow)}
            {slots.andMore}
          </>
        ) : (
          slots.items
        )
      ) : (
        slots.items
      )}
    </div>
  );
};

CaterpillarBase.displayName = 'Caterpillar';

export const Caterpillar = Object.assign(CaterpillarBase, {
  Item: CaterpillarItemSlot,
  AndMore: CaterpillarAndMoreSlot,
});
